import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Bookmark, ShoppingBag, X } from "lucide-react";
import { API, errMsg, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

const FILTERS = [["all", "All"], ["look", "Looks"], ["photo", "Beauty"], ["styling", "Styling"], ["review", "Reviews"]];

function LookModal({ post, onClose, onAct }) {
  const { user } = useAuth();
  const act = async (verb) => { if (!user) { toast.error("Log in to interact"); return; } try { const { data } = await API.post(`/community/posts/${post.id}/${verb}`); onAct(post.id, verb, data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="look-modal">
      <div className="bg-white rounded-3xl overflow-hidden max-w-3xl w-full max-h-[90vh] overflow-y-auto grid md:grid-cols-2" onClick={(e) => e.stopPropagation()}>
        {post.media?.[0] && <img src={post.media[0]} alt="" className="w-full h-full object-cover max-h-[90vh]" />}
        <div className="p-6 relative">
          <button onClick={onClose} className="absolute top-4 right-4 text-stone-400" data-testid="look-modal-close"><X className="w-5 h-5" /></button>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-[#6A1B29] text-white grid place-items-center font-semibold">{post.user_name?.[0] || "S"}</span>
            {post.username ? <Link to={`/sakhi/${post.username}`} className="text-sm font-semibold hover:text-[#6A1B29]">{post.user_name}</Link> : <p className="text-sm font-semibold">{post.user_name}</p>}
          </div>
          <p className="text-sm text-stone-700 mt-4">{post.caption}</p>
          <div className="flex items-center gap-5 mt-4 text-stone-500">
            <button onClick={() => act("like")} className="flex items-center gap-1.5 text-sm"><Heart className={`w-5 h-5 ${post.liked ? "fill-[#6A1B29] text-[#6A1B29]" : ""}`} /> {post.like_count}</button>
            <button onClick={() => act("save")} className="flex items-center gap-1.5 text-sm"><Bookmark className={`w-5 h-5 ${post.saved ? "fill-[#6A1B29] text-[#6A1B29]" : ""}`} /> {post.save_count}</button>
          </div>
          {post.products?.length > 0 && (
            <div className="mt-6">
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#6A1B29] flex items-center gap-1.5"><ShoppingBag className="w-4 h-4" /> Shop this look</p>
              <div className="space-y-2 mt-3" data-testid="shop-this-look">
                {post.products.map((pr) => (
                  <Link key={pr.id} to={`/product/${pr.slug || pr.id}`} className="flex items-center gap-3 border border-stone-200 rounded-xl p-2 hover:border-[#6A1B29]" data-testid={`shop-item-${pr.id}`}>
                    <img src={pr.image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium line-clamp-1">{pr.title}</p><p className="text-sm font-semibold text-stone-900">{inr(pr.selling_price)}</p></div>
                    <span className="text-xs font-semibold text-[#6A1B29]">Shop →</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SakhiWall() {
  const [filter, setFilter] = useState("all");
  const [posts, setPosts] = useState(null);
  const [active, setActive] = useState(null);
  useEffect(() => {
    setPosts(null);
    API.get(`/community/posts?limit=60${filter === "all" ? "" : `&type=${filter}`}`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, [filter]);
  const onAct = (id, verb, data) => {
    setPosts((ps) => ps.map((p) => p.id === id ? { ...p, liked: verb === "like" ? data.liked : p.liked, saved: verb === "save" ? data.saved : p.saved, like_count: p.like_count + (verb === "like" ? (data.liked ? 1 : -1) : 0), save_count: p.save_count + (verb === "save" ? (data.saved ? 1 : -1) : 0) } : p));
    setActive((a) => a && a.id === id ? { ...a, liked: verb === "like" ? data.liked : a.liked, saved: verb === "save" ? data.saved : a.saved, like_count: a.like_count + (verb === "like" ? (data.liked ? 1 : -1) : 0), save_count: a.save_count + (verb === "save" ? (data.saved ? 1 : -1) : 0) } : a);
  };
  return (
    <div className="bg-[#FAF6F1] min-h-[70vh]" data-testid="sakhi-wall-page">
      <Seo title="Sakhi Wall | Suvikka Sakhi" description="A visual wall of real looks, styles and finds from the Suvikka Sakhi community." canonicalPath="/sakhi-wall" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#6A1B29]">Suvikka Sakhi</p>
        <h1 className="font-display text-3xl sm:text-5xl font-semibold text-stone-900 mt-2">Sakhi Wall 💗</h1>
        <p className="text-stone-500 mt-2">Real looks from real Sakhis. Tap any look to shop it.</p>
        <div className="flex gap-2 mt-6 overflow-x-auto pb-2 -mx-1 px-1" data-testid="wall-filters">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`wall-filter-${k}`} className={`shrink-0 text-sm font-medium px-4 py-2 rounded-full transition-colors ${filter === k ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-stone-900"}`}>{l}</button>
          ))}
        </div>
        {posts === null ? (
          <div className="columns-2 md:columns-4 gap-4 mt-6">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="mb-4 rounded-2xl bg-stone-100 animate-pulse" style={{ height: 180 + (i % 3) * 60 }} />)}</div>
        ) : posts.length === 0 ? (
          <div className="mt-10 bg-white border border-stone-200 rounded-3xl p-12 text-center" data-testid="wall-empty">
            <p className="font-display text-xl text-stone-800">Be the first Sakhi to post a look ✨</p>
            <Link to="/share-look" className="inline-block mt-5 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-6 py-3 rounded-full text-sm" data-testid="wall-empty-cta">Show us your look</Link>
          </div>
        ) : (
          <div className="columns-2 md:columns-4 gap-4 mt-6" data-testid="wall-grid">
            {posts.map((p) => (
              <button key={p.id} onClick={() => setActive(p)} className="mb-4 w-full block break-inside-avoid group text-left" data-testid={`wall-tile-${p.id}`}>
                <div className="relative overflow-hidden rounded-2xl bg-stone-100">
                  {p.media?.[0] && <img src={p.media[0]} alt="" loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  {p.products?.length > 0 && <span className="absolute top-2 left-2 bg-white/90 text-stone-800 text-[10px] font-bold px-2 py-1 rounded-full inline-flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> Shop</span>}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3 text-white">
                    <p className="text-xs font-semibold truncate">{p.user_name}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      {active && <LookModal post={active} onClose={() => setActive(null)} onAct={onAct} />}
    </div>
  );
}
